import { Component, inject, Injector, Input, OnInit, ViewChild, viewChild } from '@angular/core';
import { DialogBaseComponent } from "../../base-admin-panel/dialog-base/dialog-base.component";
import { IManagableObject } from "../../IManagableObject";
import { ObjectsService } from "../../../../services/objects.service";
import { KeysService } from "../../../../services/keys.service";
import { IconViewModel } from "../../../../models/IconViewModel";
import { ObjectModel } from "../../../../models/ObjectModel";
import { CommandsService, CommandTemplateModel } from "../../../../services/commands.service";
import { ICommandTemplate } from "../../../mobile-models/profile-models";
import { ObjectGreenDrivingComponent } from './object-green-driving/object-green-driving.component';
import { AccessRightsService } from "app/services/access-rights.service";
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-objects-dialog-base',
  templateUrl: './objects-dialog-base.component.html',
  styleUrls: ['./objects-dialog-base.component.css'],
  standalone: false
})
export class ObjectsDialogBaseComponent extends DialogBaseComponent<ObjectModel> implements OnInit {

  public groups: IManagableObject[] = [];
  public keys: IManagableObject[] = [];
  public icons: IconViewModel[] = [];
  public commands: IManagableObject[] = [];
  public isLoading: boolean = true;

  protected objectsService: ObjectsService = inject(ObjectsService);
  private keysService: KeysService = inject(KeysService);
  private commandService: CommandsService = inject(CommandsService);
  private accessRights: AccessRightsService = inject(AccessRightsService);

  constructor(protected injector: Injector) {
    super(injector);
    this.model = new ObjectModel();
  }

  public openedTabName: string = "main";
  public canEditTrackers: boolean = false;
  public canEditImei: boolean = false;

  public openTab(name: string) {
    this.openedTabName = name;
  }

  ngOnInit() {
    super.ngOnInit();
    forkJoin({
      keys: this.keysService.GetKeys(),
      groups: this.objectsService.GetObjectGroups(),
      commands: this.commandService.getCommands(),
      icons: this.objectsService.GetIcons()
    }).subscribe(result => {
      this.keys = result.keys;
      this.groups = result.groups;
      this.commands = result.commands as unknown as IManagableObject[];
      this.icons = result.icons;

      this.updateEditRights();

      if (!this.model.IconId && this.icons && this.icons.length > 0) {
        this.model.IconId = this.icons[0].Id;
        this.model.TrackColor = this.icons[0].Color;
      }

      this.isLoading = false;
    });
  }

  public isSelected(name: string) {
    return name == this.openedTabName;
  }

  show(id: number) {
    this.objectsService.GetObject(id)
      .subscribe(object => {
        this.objectsService.GetIcons()
          .subscribe((icons: IconViewModel[]) => {
            this.objectsService.GetOdometer(object.Id)
              .subscribe(odometer => {
                object.Icon = icons.find(i => i.Id == object.IconId);
                object.Odometer = odometer;
                this.open(object);
              })
          })
      });
  }

  public override open(model: any, type: (new () => ObjectModel) = undefined) {
    super.open(model, type);
    this.updateEditRights();
  }

  private updateEditRights() {
    const objectId = this.model?.Id ?? 0;
    const maps = this.accessRights.buildAclMaps(this.groups ?? []);
    this.canEditTrackers = this.accessRights.canEditObject(objectId, maps);
    this.canEditImei = this.accessRights.canChangeImei(objectId, maps);
  }

  protected ModelValidate(): boolean {
    if (!this.model.Name?.trim()) {
      this.toastr.error(this.translate.instant('The name field is required'));
      return false;
    }

    if (!this.model.DeviceType?.trim()) {
      this.toastr.error(this.translate.instant('The device type field is required'));
      return false;
    }

    if (!this.isFormGreenDrivingValid()) {
      this.toastr.error(this.translate.instant('Dangerous setting must be greater than or equal to Harsh'));
      return false;
    }

    return true;
  }

  private isInvalid(
    lower: number | null | undefined,
    higher: number | null | undefined
  ): boolean {
    if (lower == null || higher == null) {
      return false;
    }

    return lower > higher;
  }

  private isMaxSpeedInvalid(): boolean {
    return this.isInvalid(this.model.MaxSpeed, this.model.DangerousSpeed);
  }

  private isDangerousSpeedInvalid(): boolean {
    return this.isInvalid(this.model.MaxSpeed, this.model.DangerousSpeed);
  }

  private isHarshAccInvalid(): boolean {
    return this.isInvalid(this.model.HarshAcceleration, this.model.DangerousAcceleration);
  }

  private isDangerousAccInvalid(): boolean {
    return this.isInvalid(this.model.HarshAcceleration, this.model.DangerousAcceleration);
  }

  private isHarshBrInvalid(): boolean {
    return this.isInvalid(this.model.HarshBreaking, this.model.DangerousBreaking);
  }

  private isDangerousBrInvalid(): boolean {
    return this.isInvalid(this.model.HarshBreaking, this.model.DangerousBreaking);
  }

  private isHarshTurnInvalid(): boolean {
    return this.isInvalid(this.model.HarshTurn, this.model.DangerousTurn);
  }

  private isDangerousTurnInvalid(): boolean {
    return this.isInvalid(this.model.HarshTurn, this.model.DangerousTurn);
  }

  public isFormGreenDrivingValid(): boolean {
    return !(
      this.isMaxSpeedInvalid() ||
      this.isDangerousSpeedInvalid() ||
      this.isHarshAccInvalid() ||
      this.isDangerousAccInvalid() ||
      this.isHarshBrInvalid() ||
      this.isDangerousBrInvalid() ||
      this.isHarshTurnInvalid() ||
      this.isDangerousTurnInvalid()
    );
  }
}
